/**
 * m.2.1 Upravljacki dashboard — fullscreen, pretty path /uprava/dash.
 * Faza 1: P&L kocka (knjig / ekon) nad /api/pl-cube/*, pivot po osima, drill do dokumenta i knjizenja,
 * pokrivenost i rupe na svakom broju. Crno-bijelo, bez grafova u boji.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../shared/api';
import { UpravaPlModule } from './upravaPlModule';
import { UpravaPlStatement } from './upravaPlStatement';
import { UpravaEkonExplorer } from './upravaEkonExplorer';

type Locale = 'hr' | 'en';

type Dim =
    | 'company_code' | 'pl_group' | 'pl_line' | 'business_type' | 'konto3' | 'konto' | 'mtp' | 'mtp_kind' | 'dept'
    | 'branch_name' | 'region' | 'country' | 'vehicle_code' | 'employee_code' | 'ym' | 'coverage' | 'gap_reason' | 'partner' | 'document';

type Mode = 'knjig' | 'sim_knjig' | 'ekon';

type Meta = {
    year: number;
    mode: Mode;
    years: number[];
    build_id: string | null;
    built_at: string | null;
    rows: number;
    gaps: number;
    by_line: Array<{ pl_group: string; pl_line: string; n: number; signed: number; gap_n: number; gap_signed: number }>;
    by_company: Array<{ company_code: string; n: number; od: string | null; do_: string | null; gap_n: number }>;
    gap_reasons: Array<{ gap_reason: string | null; n: number; signed: number }>;
    banner: string;
};

type CubeRow = { keys: Record<string, string | null>; n: number; amount: number; signed: number; gap_n: number; gap_signed: number; review_n: number };
type Cube = { dims: string[]; rows: CubeRow[]; total: { n: number; amount: number; signed: number; gap_n: number; gap_signed: number } };

type Fact = {
    id: number; company_id: number; company_code: string; doc_date: string | null; pl_line: string; business_type: string | null;
    konto: string | null; mtp: string | null; mtp_kind: string; dept: string | null; branch_name: string | null; region: string | null;
    vehicle_code: string | null; partner: string | null; document: string | null; source_table: string; source_id: number; source_ref: string | null;
    amount: number; signed: number; coverage: string; gap_reason: string | null; review: boolean; as_if: boolean;
    origin?: string; rule_code?: string | null; sim_status?: string | null; confidence?: number;
};
type Facts = { rows: Fact[]; n: number; docs: number; signed: number; limit: number; offset: number };

type Posting = { id: number; broj: string | null; konto: string; subjekt: string | null; duguje: number; potrazuje: number; datum: string; mtp: string | null; napomena: string | null; pl_line: string | null };

const DIM_LABELS: Record<Dim, [string, string]> = {
    company_code: ['Tvrtka', 'Company'],
    pl_group: ['Blok', 'Block'],
    pl_line: ['Linija iskaza', 'Statement line'],
    business_type: ['Vrsta posla', 'Business type'],
    konto3: ['Konto (3)', 'Account (3)'],
    konto: ['Konto', 'Account'],
    mtp: ['MTP', 'Cost center'],
    mtp_kind: ['Vrsta MTP', 'Cost center kind'],
    dept: ['Odjel Uprave', 'HQ department'],
    branch_name: ['Poslovnica', 'Branch'],
    region: ['Regija', 'Region'],
    country: ['Drzava', 'Country'],
    vehicle_code: ['Vozilo', 'Vehicle'],
    employee_code: ['Djelatnik', 'Employee'],
    ym: ['Mjesec', 'Month'],
    coverage: ['Pokrivenost', 'Coverage'],
    gap_reason: ['Razlog rupe', 'Gap reason'],
    partner: ['Partner', 'Partner'],
    document: ['Dokument', 'Document'],
};

const ROW_DIM_CHOICES: Dim[] = ['pl_group', 'pl_line', 'company_code', 'business_type', 'branch_name', 'region', 'dept', 'mtp', 'vehicle_code', 'konto3', 'konto', 'partner', 'gap_reason'];

const LINE_ORDER = ['prihod', 'cogs', 'opex', 'prijenos_491', 'stonex', 'da', 'rashodi_fin_ostali', 'prihod_fin', 'prihod_ostali', 'capex', 'udjeli_06', 'rezultat_79', 'konto_nepoznat'];

const LINE_LABELS: Record<string, [string, string]> = {
    prihod: ['Prihod od prodaje (75, 76)', 'Sales revenue (75, 76)'],
    cogs: ['Trosak prodane robe (70, 71)', 'Cost of goods sold (70, 71)'],
    opex: ['OPEX (razred 4 bez 43 i 491)', 'OPEX (class 4 excl. 43, 491)'],
    prijenos_491: ['Prijenos troska 491', 'Cost transfer 491'],
    stonex: ['StoneX hedging (financijski krak, u EBIT-u)', 'StoneX hedging (financial leg, in EBIT)'],
    da: ['Amortizacija (43)', 'Depreciation (43)'],
    rashodi_fin_ostali: ['Financijski i ostali rashodi (72-74)', 'Financial and other expenses (72-74)'],
    prihod_fin: ['Financijski prihodi (77)', 'Financial income (77)'],
    prihod_ostali: ['Ostali prihodi (78)', 'Other income (78)'],
    capex: ['CAPEX (razred 0, bez 06) - nije u EBIT', 'CAPEX (class 0 excl. 06) - not in EBIT'],
    udjeli_06: ['Udjeli u povezanima (06), memo', 'Shares in related parties (06), memo'],
    rezultat_79: ['Raspored rezultata (79), izvan iskaza', 'Result allocation (79), outside statement'],
    konto_nepoznat: ['Konto izvan iskaza', 'Account outside statement'],
};

const fmt = (n: number) => Number(n || 0).toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtK = (n: number) => Number(n || 0).toLocaleString('hr-HR', { maximumFractionDigits: 0 });
const pct = (part: number, whole: number) => (whole === 0 ? '' : `${Math.round((part / whole) * 1000) / 10} %`);

const css = `
.uprava-dash { height: 100%; display: flex; flex-direction: column; background: #fff; color: #111; font-family: Arial, Helvetica, sans-serif; }
.uprava-dash header { display: flex; align-items: center; gap: 24px; padding: 10px 16px; border-bottom: 1px solid #111; flex: 0 0 auto; }
.uprava-dash .brand .title { font-size: 18px; letter-spacing: 0.08em; text-transform: uppercase; }
.uprava-dash .brand .sub { font-size: 12px; color: #444; }
.uprava-dash .banner { flex: 1; font-size: 12px; padding: 6px 10px; border: 1px solid #111; background: #ececec; }
.uprava-dash nav button, .uprava-dash .exit, .uprava-dash .btn { background: #fff; border: 1px solid #111; padding: 6px 12px; font: inherit; cursor: pointer; }
.uprava-dash nav button.on, .uprava-dash .btn.on { background: #111; color: #fff; }
.uprava-dash .btn.small { padding: 2px 8px; font-size: 12px; }
.uprava-dash main { flex: 1; min-height: 0; display: flex; }
.uprava-dash aside { width: 280px; border-right: 1px solid #111; padding: 16px; box-sizing: border-box; overflow: auto; flex: 0 0 auto; }
.uprava-dash aside label { display: block; font-size: 12px; margin-bottom: 12px; }
.uprava-dash select { width: 100%; margin-top: 4px; padding: 6px; border: 1px solid #111; background: #fff; font: inherit; }
.uprava-dash .sheet { flex: 1; min-width: 0; display: flex; }
.uprava-dash .pane { flex: 1; min-width: 0; overflow: auto; border-left: 1px solid #111; }
.uprava-dash .pane:first-child { border-left: none; }
.uprava-dash .pane.narrow { flex: 0 0 38%; }
.uprava-dash table { width: 100%; border-collapse: collapse; font-size: 13px; }
.uprava-dash th, .uprava-dash td { border-bottom: 1px solid #ddd; padding: 6px 10px; text-align: left; vertical-align: top; }
.uprava-dash th { position: sticky; top: 0; background: #fff; border-bottom: 1px solid #111; font-weight: normal; z-index: 1; }
.uprava-dash .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.uprava-dash tr.total td { border-top: 1px solid #111; font-weight: bold; }
.uprava-dash tr.clickable { cursor: pointer; }
.uprava-dash tr.clickable:hover { background: #f0f0f0; }
.uprava-dash tr.active { background: #ececec; }
.uprava-dash .gap { color: #555; font-size: 11px; white-space: nowrap; }
.uprava-dash .gapmark { display: inline-block; border: 1px solid #111; padding: 0 4px; font-size: 10px; margin-left: 6px; }
.uprava-dash .note { font-size: 12px; color: #333; line-height: 1.4; }
.uprava-dash h2 { margin: 12px 16px 0; font-size: 14px; font-weight: normal; }
.uprava-dash .chips { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 16px; border-bottom: 1px solid #ddd; }
.uprava-dash .chip { border: 1px solid #111; padding: 2px 8px; font-size: 12px; cursor: pointer; background: #fff; }
.uprava-dash .chip:hover { background: #ececec; }
.uprava-dash .dims { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.uprava-dash .dims button { font-size: 12px; padding: 2px 6px; }
.uprava-dash .kpi { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; border-bottom: 1px solid #111; }
.uprava-dash .kpi div { padding: 10px 16px; border-right: 1px solid #ddd; }
.uprava-dash .kpi div:last-child { border-right: none; }
.uprava-dash .kpi .v { font-size: 18px; font-variant-numeric: tabular-nums; }
.uprava-dash .kpi .l { font-size: 11px; color: #444; }
.uprava-dash .embed { flex: 1; min-height: 0; display: flex; }
.uprava-dash .embed > * { flex: 1; }
`;

type Filters = Partial<Record<Dim, string>>;

function qs(year: string, mode: Mode, filters: Filters, extra: Record<string, string> = {}): string {
    const p = new URLSearchParams({ year, mode, ...extra });
    (Object.entries(filters) as Array<[Dim, string]>).forEach(([k, v]) => p.set(`f[${k}]`, v));
    return p.toString();
}

const keyOf = (v: string | null | undefined) => (v === null || v === undefined || v === '' ? '∅' : v);

export function UpravaDashModule({ token, locale, onExit }: { token: string; locale: Locale; onExit: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const dimLabel = (d: string) => (DIM_LABELS[d as Dim] ? DIM_LABELS[d as Dim][locale === 'en' ? 1 : 0] : d);
    const lineLabel = (l: string | null) => (l && LINE_LABELS[l] ? LINE_LABELS[l][locale === 'en' ? 1 : 0] : l || '∅');

    const [tab, setTab] = useState<'pnl' | 'coverage' | 'engine'>('pnl');
    // Zadani pogled je iskaz (fullscreen, Vedran 26.09.); pivot kocke ostaje kao sporedni alat.
    // m.2.2 (Vedran 28.09.): zadani pogled je ekonomski drill; ?view=iskaz ili ?view=kocka otvara knjig prikaz.
    const urlInit = useMemo(() => {
        try {
            const p = new URLSearchParams(window.location.search);
            const m = p.get('mode');
            const v = p.get('view');
            const mode0: Mode = m === 'ekon' || m === 'sim_knjig' || m === 'knjig' ? m : (v === 'iskaz' || v === 'kocka' ? 'knjig' : 'ekon');
            const view0: 'iskaz' | 'kocka' | 'ekon' = v === 'kocka' || v === 'iskaz' || v === 'ekon' ? v : (mode0 === 'ekon' ? 'ekon' : 'iskaz');
            return { mode0, view0 };
        } catch {
            return { mode0: 'knjig' as Mode, view0: 'iskaz' as const };
        }
    }, []);
    const [view, setView] = useState<'iskaz' | 'kocka' | 'ekon'>(urlInit.view0);
    const [year, setYear] = useState('2026');
    const [mode, setMode] = useState<Mode>(urlInit.mode0);
    const openEkon = () => { setMode('ekon'); setView('ekon'); };
    const [rowDims, setRowDims] = useState<Dim[]>(['pl_line']);
    const [byMonth, setByMonth] = useState(true);
    const [filters, setFilters] = useState<Filters>({});
    const [meta, setMeta] = useState<Meta | null>(null);
    const [cube, setCube] = useState<Cube | null>(null);
    const [facts, setFacts] = useState<Facts | null>(null);
    const [factsFor, setFactsFor] = useState<Filters | null>(null);
    const [activeFact, setActiveFact] = useState<Fact | null>(null);
    const [postings, setPostings] = useState<Posting[]>([]);
    const [err, setErr] = useState('');

    useEffect(() => {
        api<Meta>(`/pl-cube/meta?year=${encodeURIComponent(year)}&mode=${mode}`, token)
            .then((m) => { setMeta(m); setErr(''); })
            .catch((e: Error) => setErr(e.message));
    }, [token, year, mode]);

    const loadCube = useCallback(() => {
        const dims = byMonth ? [...rowDims, 'ym'] : rowDims;
        api<Cube>(`/pl-cube/cube?${qs(year, mode, filters, { dims: dims.join(',') })}`, token)
            .then((c) => { setCube(c); setErr(''); })
            .catch((e: Error) => { setCube(null); setErr(e.message); });
    }, [token, year, mode, rowDims, byMonth, filters]);

    useEffect(() => { loadCube(); }, [loadCube]);
    useEffect(() => { setFacts(null); setFactsFor(null); setActiveFact(null); setPostings([]); }, [year, mode, filters]);

    const openFacts = (extra: Filters) => {
        const f = { ...filters, ...extra };
        setFactsFor(f);
        setActiveFact(null);
        setPostings([]);
        setFacts({ rows: [], n: 0, docs: 0, signed: 0, limit: 0, offset: 0 });
        api<Facts>(`/pl-cube/facts?${qs(year, mode, f, { limit: '300' })}`, token)
            .then(setFacts)
            .catch((e: Error) => { setFacts(null); setErr(e.message); });
    };

    const openPostings = (f: Fact) => {
        setActiveFact(f);
        setPostings([]);
        // simulirani / reklasificirani redak: pravilo i ocekivano knjizenje umjesto temeljnice (treci audit M13)
        if (f.origin && f.origin !== 'booked') {
            api<{ rule: { code: string; title: string } | null; expected_posting: { debit: string | null; credit: string | null; amount: number }; gl_postings_same_document: Posting[] }>(`/pl-cube/event?fact_id=${f.id}`, token)
                .then((e) => setPostings([
                    { id: -1, broj: e.rule?.code ?? f.rule_code ?? null, konto: e.expected_posting.debit ?? '-', subjekt: `${t('ocekivano Dr', 'expected Dr')} - ${e.rule?.title ?? ''}`, duguje: e.expected_posting.amount, potrazuje: 0, datum: f.doc_date ?? '', mtp: f.mtp, napomena: f.sim_status ?? null, pl_line: f.pl_line },
                    { id: -2, broj: e.rule?.code ?? f.rule_code ?? null, konto: e.expected_posting.credit ?? '-', subjekt: t('ocekivano Cr', 'expected Cr'), duguje: 0, potrazuje: e.expected_posting.amount, datum: f.doc_date ?? '', mtp: f.mtp, napomena: f.sim_status ?? null, pl_line: f.pl_line },
                    ...(e.gl_postings_same_document || []),
                ]))
                .catch(() => setPostings([]));
            return;
        }
        if (f.source_table !== 'bi_gl_journal_lines') return;
        api<{ rows: Posting[] }>(f.document ? `/pl-cube/document?year=${encodeURIComponent(year)}&company_id=${f.company_id}&document=${encodeURIComponent(f.document)}` : `/pl-cube/document?year=${encodeURIComponent(year)}&gl_id=${f.source_id}`, token)
            .then((r) => setPostings(r.rows || []))
            .catch(() => setPostings([]));
    };

    const addFilter = (dim: Dim, value: string | null) => {
        setFilters((f) => ({ ...f, [dim]: value === null || value === '' ? '' : value }));
        setRowDims((ds) => {
            const rest = ds.filter((d) => d !== dim);
            if (rest.length > 0) return rest;
            const next = ROW_DIM_CHOICES.find((d) => d !== dim && !(d in filters));
            return next ? [next] : ds;
        });
    };
    const removeFilter = (dim: Dim) => setFilters((f) => { const n = { ...f }; delete n[dim]; return n; });
    const toggleRowDim = (d: Dim) => setRowDims((ds) => (ds.includes(d) ? (ds.length > 1 ? ds.filter((x) => x !== d) : ds) : [...ds, d]));

    // Pivot: redovi po rowDims, stupci po ym.
    const pivot = useMemo(() => {
        if (!cube) return null;
        const months = new Set<string>();
        const rows = new Map<string, { keys: Record<string, string | null>; cells: Record<string, CubeRow>; signed: number; n: number; gap_n: number; gap_signed: number; review_n: number }>();
        cube.rows.forEach((r) => {
            const ym = byMonth ? keyOf(r.keys.ym) : '';
            if (byMonth) months.add(ym);
            const rk = rowDims.map((d) => keyOf(r.keys[d])).join('|');
            let row = rows.get(rk);
            if (!row) {
                const keys: Record<string, string | null> = {};
                rowDims.forEach((d) => { keys[d] = r.keys[d] ?? null; });
                row = { keys, cells: {}, signed: 0, n: 0, gap_n: 0, gap_signed: 0, review_n: 0 };
                rows.set(rk, row);
            }
            row.cells[ym] = row.cells[ym]
                ? { ...row.cells[ym], signed: row.cells[ym].signed + r.signed, n: row.cells[ym].n + r.n, gap_n: row.cells[ym].gap_n + r.gap_n, gap_signed: row.cells[ym].gap_signed + r.gap_signed }
                : r;
            row.signed += r.signed; row.n += r.n; row.gap_n += r.gap_n; row.gap_signed += r.gap_signed; row.review_n += r.review_n;
        });
        const cols = Array.from(months).sort();
        const list = Array.from(rows.values());
        if (rowDims[0] === 'pl_line') {
            list.sort((a, b) => LINE_ORDER.indexOf(a.keys.pl_line || '') - LINE_ORDER.indexOf(b.keys.pl_line || '') || Math.abs(b.signed) - Math.abs(a.signed));
        } else {
            list.sort((a, b) => Math.abs(b.signed) - Math.abs(a.signed));
        }
        const colTotals: Record<string, number> = {};
        list.forEach((r) => Object.entries(r.cells).forEach(([ym, c]) => { colTotals[ym] = (colTotals[ym] || 0) + c.signed; }));
        return { cols, list, colTotals };
    }, [cube, rowDims, byMonth]);

    const coveragePct = meta && meta.rows > 0 ? Math.round(((meta.rows - meta.gaps) / meta.rows) * 1000) / 10 : null;
    const ebit = useMemo(() => {
        if (!meta) return null;
        // EBIT bez financijskih linija (77, 72-74); kartice su za cijelu godinu i rezim, bez filtera (drugi audit M10)
        const inEbit = new Set(['prihod', 'cogs', 'opex', 'prijenos_491', 'stonex', 'da', 'prihod_ostali']);
        let rev = 0; let ebitv = 0; let ebitda = 0;
        meta.by_line.forEach((l) => {
            if (!inEbit.has(l.pl_line)) return;
            // MySQL DECIMAL stize kao string kroz JSON; bez Number() je zbroj konkatenacija i kartice pokazu NaN.
            const v = Number(l.signed) || 0;
            ebitv += v;
            if (l.pl_line !== 'da') ebitda += v;
            if (l.pl_line === 'prihod') rev += v;
        });
        return { rev, ebitda, ebit: ebitv };
    }, [meta]);

    const cellValue = (c: CubeRow | undefined) => (c ? (
        <>
            {fmt(c.signed)}
            {c.gap_n > 0 ? <span className="gapmark" title={`${c.gap_n} ${t('redaka u rupi', 'rows in gap')}, ${fmt(c.gap_signed)} EUR`}>{t('rupa', 'gap')} {c.gap_n}</span> : null}
        </>
    ) : '');

    if (view === 'iskaz') {
        return <UpravaPlStatement token={token} locale={locale} onExit={onExit} onCube={() => setView('kocka')} onEkon={openEkon} />;
    }
    if (view === 'ekon') {
        return (
            <UpravaEkonExplorer token={token} locale={locale} year={Number(year)} mode={mode} years={meta?.years}
                onYear={(y) => setYear(String(y))} onMode={setMode} onExit={onExit}
                onStatement={() => setView('iskaz')} onCube={() => setView('kocka')} />
        );
    }

    return (
        <div className="uprava-dash">
            <style>{css}</style>
            <header>
                <button type="button" className="exit" onClick={onExit}>{t('Izlaz', 'Exit')}</button>
                <div className="brand">
                    <div className="title">Uprava</div>
                    <div className="sub">{t('Upravljacki dashboard', 'Board dashboard')}</div>
                </div>
                <div className="banner">{meta?.banner || 'INTERNO / U RAZRADI. Nije GFI.'}{meta?.built_at ? ` ${t('Kocka izgradjena', 'Cube built')} ${meta.built_at}.` : ''}</div>
                <nav>
                    <button type="button" onClick={() => setView('iskaz')}>{t('Iskaz', 'Statement')}</button>
                    <button type="button" onClick={openEkon}>{t('Ekonomski drill', 'Economic drill')}</button>
                    <button type="button" className={tab === 'pnl' ? 'on' : ''} onClick={() => setTab('pnl')}>{t('Kocka', 'Cube')}</button>
                    <button type="button" className={tab === 'coverage' ? 'on' : ''} onClick={() => setTab('coverage')}>{t('Pokrivenost', 'Coverage')}</button>
                    <button type="button" className={tab === 'engine' ? 'on' : ''} onClick={() => setTab('engine')}>{t('Iskaz (motor)', 'Statement (engine)')}</button>
                </nav>
            </header>
            {tab === 'engine' ? (
                <div className="embed"><UpravaPlModule token={token} locale={locale} onExit={() => setTab('pnl')} /></div>
            ) : null}
            {tab !== 'engine' ? (
                <main>
                    <aside>
                        <label>{t('Godina', 'Year')}
                            <select value={year} onChange={(e) => setYear(e.target.value)}>
                                {(meta?.years?.length ? meta.years.map(String) : [year]).map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </label>
                        <label>{t('Rezim', 'Mode')}
                            <div className="dims">
                                <button type="button" className={`btn small ${mode === 'knjig' ? 'on' : ''}`} onClick={() => setMode('knjig')}>{t('Knjigovodstveni', 'Book')}</button>
                                <button type="button" className={`btn small ${mode === 'sim_knjig' ? 'on' : ''}`} onClick={() => setMode('sim_knjig')}>{t('Sim. knjig.', 'Sim. book')}</button>
                                <button type="button" className={`btn small ${mode === 'ekon' ? 'on' : ''}`} onClick={() => setMode('ekon')}>{t('Sim. ekon.', 'Sim. econ.')}</button>
                            </div>
                        </label>
                        <label>{t('Redovi (redom)', 'Rows (in order)')}
                            <div className="dims">
                                {ROW_DIM_CHOICES.map((d) => (
                                    <button key={d} type="button" className={`btn small ${rowDims.includes(d) ? 'on' : ''}`} onClick={() => toggleRowDim(d)} disabled={d in filters}>
                                        {rowDims.includes(d) ? `${rowDims.indexOf(d) + 1}. ` : ''}{dimLabel(d)}
                                    </button>
                                ))}
                            </div>
                        </label>
                        <label>
                            <input type="checkbox" checked={byMonth} onChange={(e) => setByMonth(e.target.checked)} /> {t('Stupci po mjesecu', 'Columns by month')}
                        </label>
                        <p className="note">{err || t('Klik na vrijednost u redu postavlja filter i spusta razinu. Klik na iznos otvara dokumente te celije. Predznak: prihod plus, trosak minus. Rupa = redak bez MTP-a ili s neimenovanim MTP-om; iznos je u zbroju, os nije.', 'Click a row value to filter and drill. Click an amount to open its documents. Sign: revenue plus, cost minus. Gap = row without cost center or with unnamed one; amount is in the total, axis is not.')}</p>
                        {meta ? (
                            <p className="note">
                                {t('Kocka', 'Cube')}: {fmtK(meta.rows)} {t('redaka', 'rows')}, {fmtK(meta.gaps)} {t('u rupi', 'in gap')} ({coveragePct} % {t('pokriveno', 'covered')}).<br />
                                {meta.by_company.map((c) => `${c.company_code}: ${fmtK(c.n)} (${c.od || '?'} .. ${c.do_ || '?'})`).join('; ')}
                            </p>
                        ) : null}
                        {meta && meta.rows === 0 ? <p className="note">{t('Kocka za ovaj rezim i godinu nije izgradjena. Naredba: php artisan bi:pl-cube-build --year=', 'Cube for this mode and year is not built. Command: php artisan bi:pl-cube-build --year=')}{year} --mode={mode}</p> : null}
                    </aside>
                    {tab === 'pnl' ? (
                        <div className="sheet">
                            <div className="pane">
                                {ebit ? (
                                    <div className="kpi">
                                        <div><div className="v">{fmt(ebit.rev)}</div><div className="l">{t('Prihod od prodaje', 'Sales revenue')} {year} ({mode})</div></div>
                                        <div><div className="v">{fmt(ebit.ebitda)}</div><div className="l">{t('EBITDA (godina, bez filtera)', 'EBITDA (year, unfiltered)')}</div></div>
                                        <div><div className="v">{fmt(ebit.ebit)}</div><div className="l">{t('EBIT (bez financijskih, bez filtera)', 'EBIT (excl. financial, unfiltered)')}</div></div>
                                        <div><div className="v">{coveragePct === null ? '' : `${coveragePct} %`}</div><div className="l">{t('Pokrivenost osima', 'Axis coverage')} ({fmtK(meta?.gaps || 0)} {t('rupa', 'gaps')})</div></div>
                                    </div>
                                ) : null}
                                <div className="chips">
                                    <span className="note">{t('Filteri', 'Filters')}:</span>
                                    {(Object.entries(filters) as Array<[Dim, string]>).map(([d, v]) => (
                                        <button key={d} type="button" className="chip" onClick={() => removeFilter(d)}>{dimLabel(d)} = {d === 'pl_line' ? lineLabel(v || null) : (v === '' ? '∅' : v)} ×</button>
                                    ))}
                                    {Object.keys(filters).length === 0 ? <span className="note">{t('nema', 'none')}</span> : null}
                                </div>
                                <table>
                                    <thead>
                                        <tr>
                                            {rowDims.map((d) => <th key={d}>{dimLabel(d)}</th>)}
                                            {(pivot?.cols || []).map((c) => <th key={c} className="num">{c}</th>)}
                                            <th className="num">{t('Ukupno', 'Total')}</th>
                                            <th className="num">n</th>
                                            <th className="num">{t('Rupa', 'Gap')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(pivot?.list || []).map((r, i) => (
                                            <tr key={i}>
                                                {rowDims.map((d) => (
                                                    <td key={d}>
                                                        <button type="button" className="chip" onClick={() => addFilter(d, r.keys[d])}>
                                                            {d === 'pl_line' ? lineLabel(r.keys[d]) : keyOf(r.keys[d])}
                                                        </button>
                                                    </td>
                                                ))}
                                                {(pivot?.cols || []).map((c) => (
                                                    <td key={c} className="num" style={{ cursor: 'pointer' }} onClick={() => openFacts({ ...Object.fromEntries(rowDims.map((d) => [d, r.keys[d] ?? ''])), ym: c === '∅' ? '' : c })}>
                                                        {cellValue(r.cells[c])}
                                                    </td>
                                                ))}
                                                <td className="num" style={{ cursor: 'pointer' }} onClick={() => openFacts(Object.fromEntries(rowDims.map((d) => [d, r.keys[d] ?? ''])))}>{fmt(r.signed)}</td>
                                                <td className="num">{fmtK(r.n)}</td>
                                                <td className="num gap">{r.gap_n > 0 ? `${fmtK(r.gap_n)} / ${fmt(r.gap_signed)}` : ''}</td>
                                            </tr>
                                        ))}
                                        {cube ? (
                                            <tr className="total">
                                                <td colSpan={rowDims.length}>{t('Ukupno', 'Total')}</td>
                                                {(pivot?.cols || []).map((c) => <td key={c} className="num">{fmt(pivot?.colTotals[c] || 0)}</td>)}
                                                <td className="num">{fmt(cube.total.signed)}</td>
                                                <td className="num">{fmtK(cube.total.n)}</td>
                                                <td className="num gap">{cube.total.gap_n > 0 ? `${fmtK(cube.total.gap_n)} / ${fmt(cube.total.gap_signed)} (${pct(cube.total.gap_n, cube.total.n)})` : ''}</td>
                                            </tr>
                                        ) : null}
                                    </tbody>
                                </table>
                            </div>
                            <div className="pane narrow">
                                <h2>{t('Dokumenti', 'Documents')}{factsFor ? `: ${(Object.entries(factsFor) as Array<[Dim, string]>).map(([d, v]) => `${dimLabel(d)}=${v === '' ? '∅' : v}`).join(', ')}` : ''}</h2>
                                <p className="note" style={{ margin: '4px 16px' }}>
                                    {facts ? `${fmtK(facts.docs)} ${t('dokumenata', 'documents')}, ${fmtK(facts.n)} ${t('redaka', 'rows')}, ${fmt(facts.signed)} EUR${facts.rows.length < facts.n ? ` (${t('prikazano', 'shown')} ${facts.rows.length})` : ''}.` : t('Klik na iznos u kocki otvara dokumente.', 'Click an amount in the cube to open documents.')}
                                </p>
                                <table>
                                    <thead><tr><th>{t('Datum', 'Date')}</th><th>{t('Dokument', 'Document')}</th><th>{t('Partner', 'Partner')}</th><th>{t('Konto', 'Account')}</th><th>MTP</th><th className="num">EUR</th></tr></thead>
                                    <tbody>
                                        {(facts?.rows || []).map((f) => (
                                            <tr key={f.id} className={['clickable', activeFact?.id === f.id ? 'active' : ''].join(' ')} onClick={() => openPostings(f)}>
                                                <td>{f.doc_date || ''}</td>
                                                <td>{f.document || ''}{f.coverage === 'gap' ? <span className="gapmark">{f.gap_reason}</span> : null}</td>
                                                <td>{f.partner || ''}</td>
                                                <td>{f.konto || ''}</td>
                                                <td>{f.mtp || ''}{f.branch_name ? ` ${f.branch_name}` : f.dept ? ` ${f.dept}` : ''}</td>
                                                <td className="num">{fmt(f.signed)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {activeFact ? (
                                    <>
                                        <h2>{t('Knjizenja', 'Postings')}: {activeFact.document} ({activeFact.company_code}, {activeFact.source_table})</h2>
                                        <table>
                                            <thead><tr><th>{t('Konto', 'Account')}</th><th>{t('Linija', 'Line')}</th><th>MTP</th><th>{t('Partner', 'Partner')}</th><th className="num">{t('Duguje', 'Debit')}</th><th className="num">{t('Potrazuje', 'Credit')}</th></tr></thead>
                                            <tbody>
                                                {postings.map((p) => (
                                                    <tr key={p.id}>
                                                        <td>{p.konto}</td>
                                                        <td>{p.pl_line ? lineLabel(p.pl_line) : ''}</td>
                                                        <td>{p.mtp || ''}</td>
                                                        <td>{p.subjekt || ''}{p.napomena ? ` · ${p.napomena}` : ''}</td>
                                                        <td className="num">{p.duguje ? fmt(p.duguje) : ''}</td>
                                                        <td className="num">{p.potrazuje ? fmt(p.potrazuje) : ''}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {postings.length === 0 ? <p className="note" style={{ margin: '4px 16px' }}>{t('Nema knjizenja za prikaz (izvor nije GL ili dokument nema oznaku).', 'No postings to show (source is not GL or document has no reference).')}</p> : null}
                                    </>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                    {tab === 'coverage' ? (
                        <div className="sheet">
                            <div className="pane">
                                <h2>{t('Linije iskaza: redci, iznos, rupe', 'Statement lines: rows, amount, gaps')}</h2>
                                <table>
                                    <thead><tr><th>{t('Blok', 'Block')}</th><th>{t('Linija', 'Line')}</th><th className="num">n</th><th className="num">EUR</th><th className="num">{t('Rupa n', 'Gap n')}</th><th className="num">{t('Rupa EUR', 'Gap EUR')}</th><th className="num">{t('Pokriveno', 'Covered')}</th></tr></thead>
                                    <tbody>
                                        {(meta?.by_line || []).map((l) => (
                                            <tr key={l.pl_line} className="clickable" onClick={() => { setFilters({ pl_line: l.pl_line }); setRowDims(['gap_reason', 'mtp']); setTab('pnl'); }}>
                                                <td>{l.pl_group}</td><td>{lineLabel(l.pl_line)}</td>
                                                <td className="num">{fmtK(l.n)}</td><td className="num">{fmt(l.signed)}</td>
                                                <td className="num">{fmtK(l.gap_n)}</td><td className="num">{fmt(l.gap_signed)}</td>
                                                <td className="num">{pct(l.n - l.gap_n, l.n)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <h2>{t('Razlozi rupa', 'Gap reasons')}</h2>
                                <table>
                                    <thead><tr><th>{t('Razlog', 'Reason')}</th><th className="num">n</th><th className="num">EUR</th></tr></thead>
                                    <tbody>
                                        {(meta?.gap_reasons || []).map((g) => (
                                            <tr key={g.gap_reason || 'null'} className="clickable" onClick={() => { setFilters({ gap_reason: g.gap_reason || '' }); setRowDims(['company_code', 'mtp', 'konto3']); setTab('pnl'); }}>
                                                <td>{g.gap_reason || '∅'}</td><td className="num">{fmtK(g.n)}</td><td className="num">{fmt(g.signed)}</td>
                                            </tr>
                                        ))}
                                        {meta && meta.gap_reasons.length === 0 ? <tr><td colSpan={3}>{t('Nema rupa.', 'No gaps.')}</td></tr> : null}
                                    </tbody>
                                </table>
                                <p className="note" style={{ margin: '12px 16px' }}>
                                    {t('nomtp = knjizenje bez sifre MTP-a (ide na Upravu, os poslovnice nedostaje). mtp_neimenovan = MTP 300/301 koje Vedran jos nije imenovao. konto_izvan_iskaza = razred 7 bez podrazreda ili 79. Simulirani rezimi (sim. knjig., sim. ekon.) grade se naredbom bi:pl-cube-build --mode=all; dok nisu izgradjeni vracaju praznu kocku, ne procjenu.', 'nomtp = posting without cost center (goes to HQ, branch axis missing). mtp_neimenovan = cost centers 300/301 not yet named. konto_izvan_iskaza = class 7 without subclass or 79. Simulated modes are built by bi:pl-cube-build --mode=all; until built they return an empty cube, never an estimate.')}
                                </p>
                            </div>
                        </div>
                    ) : null}
                </main>
            ) : null}
        </div>
    );
}
